import React from "react";
import { ClockIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { billingService } from "../../services/billingService";

interface TrialBannerProps {
  isTrialActive: boolean;
  trialEndDate?: string;
  hasGenAiAccess: boolean;
  onUpgrade?: () => void;
  hasActiveSubscription?: boolean;
}

export const TrialBanner: React.FC<TrialBannerProps> = ({
  isTrialActive,
  trialEndDate,
  hasGenAiAccess,
  onUpgrade,
  hasActiveSubscription = false,
}) => {
  if (!isTrialActive || !trialEndDate) {
    return null;
  }

  const daysRemaining =
    billingService.calculateTrialDaysRemaining(trialEndDate);
  const isExpiringSoon = daysRemaining <= 3;

  const getBannerColor = () => {
    if (isExpiringSoon) return "bg-red-50 border-red-200";
    if (daysRemaining <= 7) return "bg-yellow-50 border-yellow-200";
    return "bg-blue-50 border-blue-200";
  };

  const getTextColor = () => {
    if (isExpiringSoon) return "text-red-800";
    if (daysRemaining <= 7) return "text-yellow-800";
    return "text-blue-800";
  };

  const getIconColor = () => {
    if (isExpiringSoon) return "text-red-500";
    if (daysRemaining <= 7) return "text-yellow-500";
    return "text-blue-500";
  };

  return (
    <div className={`rounded-lg p-4 border ${getBannerColor()}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {hasGenAiAccess ? (
            <SparklesIcon className={`h-5 w-5 ${getIconColor()}`} />
          ) : (
            <ClockIcon className={`h-5 w-5 ${getIconColor()}`} />
          )}
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${getTextColor()}`}>
            Trial Active
          </h3>
          <div className={`mt-1 text-sm ${getTextColor()}`}>
            <p>
              {daysRemaining === 0
                ? "Your trial expires today!"
                : daysRemaining === 1
                  ? "Your trial expires tomorrow!"
                  : `${daysRemaining} days remaining in your trial`}
            </p>
            {hasGenAiAccess && (
              <p className="mt-1">
                You have full access to AI-powered research features during your
                trial.
              </p>
            )}
          </div>
          {!hasActiveSubscription && (
            <div className="mt-3">
              <p className={`text-sm ${getTextColor()}`}>
                To continue access after your trial, use the Manage Subscription
                section below to select a plan.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
